import { useEffect, useRef, useState } from 'react'
import { Controller, type FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Alert, Button, Card, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import 'dayjs/locale/ru'
import { toast } from 'react-toastify'
import { FormBlock } from '../../../../common/FormGenerator'
import { getFormConfig } from './settingsConfig'
import { CardStyle, TypStyle } from './style'
import {
  type UserFormOutput,
  type UserFormValues,
  userFormSchema,
  userFormToPayload,
  userToFormValues,
} from '../ZodValidationSchema'
import type { UserDescription } from '../../../../types/entitys/users'
import {
  useGetCompetenciesDictionaryQuery,
  useGetImprovementsDictionaryQuery,
  useGetParticipationsDictionaryQuery,
  useGetResourcesDictionaryQuery,
  useDeleteMyAvatarMutation,
  useUploadMyAvatarMutation,
  useUpdateUserProfileMutation,
} from '../../../../api'
import { buildUpdateUserRequestBody } from '../Mappers/mapProfileUpdateBody'
import { extractApiErrorMessage } from '../../../../utils/api'
import { isProfileFormUnchanged } from '../../../../utils/profile/areProfileFormOutputsEqual'
import { BirthdayField } from './BirthdayField'
import { SlugField } from './SlugField'
import { DictionaryMultiSelect } from '../../../../components/ui-kit/DictionaryMultiSelect'

type UserSettingsFormProps = {
  initialData: UserDescription
}

export const UserSettingsForm = ({ initialData }: UserSettingsFormProps) => {
  const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const
  const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024

  const toImageBaselineSignature = (value: UserFormOutput['image']): string => {
    if (value instanceof File) {
      return `file:${value.name}:${value.size}:${value.type}:${value.lastModified}`
    }
    if (value == null) {
      return 'null'
    }
    return `url:${String(value).trim()}`
  }

  const validateImageFile = (file: File): string | null => {
    if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number])) {
      return 'Некорректный формат изображения. Разрешены JPG, PNG, WEBP, GIF'
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      return 'Размер изображения не должен превышать 5 МБ'
    }
    return null
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const baselineRef = useRef<UserFormOutput | null>(null)
  const imageBaselineRef = useRef<string>('null')
  const [updateUserProfile] = useUpdateUserProfileMutation()
  const [uploadMyAvatar] = useUploadMyAvatarMutation()
  const [deleteMyAvatar] = useDeleteMyAvatarMutation()

  const qComp = useGetCompetenciesDictionaryQuery()
  const qRes = useGetResourcesDictionaryQuery()
  const qPart = useGetParticipationsDictionaryQuery()
  const qImp = useGetImprovementsDictionaryQuery()

  const methods = useForm<UserFormValues, unknown, UserFormOutput>({
    mode: 'onBlur',
    resolver: zodResolver(userFormSchema),
    defaultValues: userToFormValues(initialData),
  })

  const { handleSubmit, reset, watch } = methods
  const watchedImage = watch('image')

  const dictsError = qComp.isError || qRes.isError || qPart.isError || qImp.isError
  const dictsReady =
    qComp.isSuccess &&
    qRes.isSuccess &&
    qPart.isSuccess &&
    qImp.isSuccess

  useEffect(() => {
    if (!dictsReady || !qComp.data || !qRes.data || !qPart.data || !qImp.data) {
      return
    }
    const values = userToFormValues(initialData, {
      competencies: qComp.data,
      resources: qRes.data,
      participations: qPart.data,
      improvements: qImp.data,
    })
    reset(values)
    const parsed = userFormSchema.safeParse(values)
    baselineRef.current = parsed.success ? parsed.data : null
    imageBaselineRef.current = parsed.success
      ? toImageBaselineSignature(parsed.data.image)
      : 'null'
    setImageFile(null)
  }, [
    initialData,
    dictsReady,
    qComp.data,
    qRes.data,
    qPart.data,
    qImp.data,
    reset,
  ])

  useEffect(() => {
    setImageFile(watchedImage instanceof File ? watchedImage : null)
  }, [watchedImage])

  const findFirstValidationMessage = (
    errors: FieldErrors<UserFormValues>
  ): string | undefined => {
    const fieldOrder: (keyof UserFormValues)[] = [
      'image',
      'surname',
      'name',
      'otch',
      'slug',
      'status',
      'birthday',
      'slogan',
      'region',
      'city',
      'work',
      'description',
      'full_description',
      'competencyIds',
      'resourceIds',
      'participationIds',
      'improvementIds',
      'matchingRemoteAllowed',
    ]

    for (let i = 0; i < fieldOrder.length; i += 1) {
      const message = errors[fieldOrder[i]]?.message
      if (message) {
        return String(message)
      }
    }

    return undefined
  }

  const onInvalid = (errors: FieldErrors<UserFormValues>) => {
    toast.error(findFirstValidationMessage(errors) ?? 'Проверьте заполнение формы')
  }

  const onSubmit = async (data: UserFormOutput) => {
    if (!dictsReady) {
      toast.error('Справочники ещё загружаются')
      return
    }

    const isImageChanged = toImageBaselineSignature(data.image) !== imageBaselineRef.current

    if (isImageChanged && imageFile != null) {
      const imageValidationError = validateImageFile(imageFile)
      if (imageValidationError) {
        toast.error(imageValidationError)
        return
      }
    }

    const profileUnchanged =
      baselineRef.current != null && isProfileFormUnchanged(data, baselineRef.current)

    if (profileUnchanged && !isImageChanged) {
      toast.info('Нет изменений')
      return
    }

    setIsSubmitting(true)
    try {
      const payload = userFormToPayload(data)
      const jsonBody = buildUpdateUserRequestBody(payload, initialData)
      if (!profileUnchanged) {
        await updateUserProfile({ userId: initialData.id, body: jsonBody }).unwrap()
      }

      if (isImageChanged) {
        if (imageFile != null) {
          await uploadMyAvatar(imageFile).unwrap()
        } else {
          await deleteMyAvatar().unwrap()
        }
      }

      baselineRef.current = data
      imageBaselineRef.current = toImageBaselineSignature(data.image)
      setImageFile(data.image instanceof File ? data.image : null)
      toast.success('Профиль сохранён')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить изменения')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!dictsError && !dictsReady) {
    return (
      <Stack alignItems="center" justifyContent="center" height="100%">
        <CircularProgress />
      </Stack>
    )
  }

  if (dictsError || !qComp.data || !qRes.data || !qPart.data || !qImp.data) {
    return (
      <Typography color="error" textAlign="center" px={2}>
        Не удалось загрузить справочники. Обновите страницу.
      </Typography>
    )
  }

  return (
    <Stack alignItems="center" pt={{ xs: '15px', md: '50px' }} px={1.5}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
        <FormProvider {...methods}>
          <Stack
            gap={2}
            maxWidth={{ xs: '736px', md: '840px' }}
            width="100%"
            component="form"
            onSubmit={handleSubmit(onSubmit, onInvalid)}
          >
          <Typography variant="h1" px={{ xs: 0, md: 2.5 }}>
            Настройки
          </Typography>

          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Основное
            </Typography>
            <Stack gap={1.5}>
              <FormBlock formFieldConfig={getFormConfig('image')} />
              <FormBlock formFieldConfig={getFormConfig('surname')} />
              <FormBlock formFieldConfig={getFormConfig('name')} />
              <FormBlock formFieldConfig={getFormConfig('otch')} />
              <SlugField />
              <BirthdayField />
              <FormBlock formFieldConfig={getFormConfig('slogan')} />
              <FormBlock formFieldConfig={getFormConfig('work')} />
              <FormBlock formFieldConfig={getFormConfig('region')} />
              <FormBlock formFieldConfig={getFormConfig('city')} />
            </Stack>
          </Card>

          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              О себе
            </Typography>
            <Stack gap={2}>
              <Stack gap={0.75}>
                <Typography variant="black-14-medium" component="div">
                  Кратко о себе
                </Typography>
                <Typography variant="black-14-normal" color="text.secondary">
                  До 500 символов — краткий текст в карточке профиля
                </Typography>
                <FormBlock formFieldConfig={getFormConfig('description')} />
              </Stack>
              <Stack gap={0.75}>
                <Typography variant="black-14-medium" component="div">
                  Подробное описание
                </Typography>
                <Typography variant="black-14-normal" color="text.secondary">
                  Полный текст; открывается по «Читать далее», если отличается от краткого
                </Typography>
                <FormBlock formFieldConfig={getFormConfig('full_description')} />
              </Stack>
            </Stack>
          </Card>

          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Рекомендации
            </Typography>
            <Stack gap={2}>
              <Alert severity="info">
                Чем точнее заполнен этот блок, тем точнее подбор проектов и приглашений.
              </Alert>
              <Stack gap={0.75}>
                <Typography variant="black-14-medium" component="div">
                  Статус участия
                </Typography>
                <FormBlock formFieldConfig={getFormConfig('status')} />
              </Stack>
              <Controller
                name="matchingRemoteAllowed"
                control={methods.control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />}
                    label="Можно удалённо"
                  />
                )}
              />
              <Stack gap={0.75}>
                <Typography variant="black-14-medium" component="div">
                  Компетенции
                </Typography>
                <DictionaryMultiSelect
                  name="competencyIds"
                  dictionary={qComp.data}
                  placeholder="Выберите из списка"
                />
              </Stack>
              <Stack gap={0.75}>
                <Typography variant="black-14-medium" component="div">
                  Доступные ресурсы
                </Typography>
                <DictionaryMultiSelect
                  name="resourceIds"
                  dictionary={qRes.data}
                  placeholder="Выберите из списка"
                />
              </Stack>
              <Stack gap={0.75}>
                <Typography variant="black-14-medium" component="div">
                  Условия участия
                </Typography>
                <DictionaryMultiSelect
                  name="participationIds"
                  dictionary={qPart.data}
                  placeholder="Выберите из списка"
                />
              </Stack>
            </Stack>
          </Card>

          <Card variant="primaryAdaptiveCard" sx={CardStyle}>
            <Typography variant="black-16-semiBold" component="div" sx={TypStyle}>
              Направления развития
            </Typography>
            <DictionaryMultiSelect
              name="improvementIds"
              dictionary={qImp.data}
              placeholder="Выберите из списка"
            />
          </Card>

          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="follow" disabled={isSubmitting}>
              {isSubmitting ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} color="inherit" />
                  <span>Сохранение...</span>
                </Stack>
              ) : (
                'Сохранить'
              )}
            </Button>
          </Stack>
          </Stack>
        </FormProvider>
      </LocalizationProvider>
    </Stack>
  )
}
